import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function Surstock() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchProduits();
        fetchCategories();
    }, [filterCategorie]);

    const fetchProduits = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/stock_alertes.php?action=surstock`;
            
            const params = new URLSearchParams();
            if (filterCategorie) params.append('categorie', filterCategorie);
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur catégories", err);
        }
    };

    const filteredProduits = produits.filter(p => 
        p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
        p.designation?.toLowerCase().includes(filterText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="rounded-3">
                <div className="d-flex align-items-center">
                    <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                    <div>
                        <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                        <p className="mb-0 small">{error}</p>
                        <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchProduits}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
            </Alert>
        );
    }

    return (
        <>
            {/* Filtres modernes */}
            <Row className="g-3 mb-4">
                <Col md={5}>
                    <div className="position-relative">
                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="ps-5 rounded-pill"
                        />
                    </div>
                </Col>
                <Col md={4}>
                    <Form.Select 
                        value={filterCategorie}
                        onChange={(e) => setFilterCategorie(e.target.value)}
                        className="rounded-pill"
                    >
                        <option value="">Toutes catégories</option>
                        {categories.map(c => (
                            <option key={c.id_categorie} value={c.id_categorie}>
                                {c.nom_categorie}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => {
                            setFilterText('');
                            setFilterCategorie('');
                        }} 
                        className="w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                    >
                        <i className="ri-refresh-line"></i>
                        <span>Réinitialiser</span>
                    </Button>
                </Col>
            </Row>

            {/* Tableau moderne */}
            <div className="table-responsive">
                <Table hover className="align-middle mb-0 custom-table">
                    <thead className="bg-light">
                        <tr>
                            <th className="ps-4">Référence</th>
                            <th>Produit</th>
                            <th>Catégorie</th>
                            <th className="text-center">Stock actuel</th>
                            <th className="text-center">Max recommandé</th>
                            <th className="text-center">Excédent</th>
                            <th className="text-center">Utilisation</th>
                            <th>Valeur immobilisée</th>
                            <th className="text-center pe-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProduits.length > 0 ? (
                            filteredProduits.map((p, index) => {
                                const ratio = (p.stock_maximum / p.quantite_reelle) * 100;
                                return (
                                    <tr key={p.id_produit} style={{
                                        animation: `fadeIn 0.3s ease ${index * 0.03}s both`
                                    }}>
                                        <td className="ps-4">
                                            <span className="reference-badge">{p.reference}</span>
                                        </td>
                                        <td>
                                            <Link to={`/stock/produits/${p.id_produit}`} className="table-link">
                                                {p.designation}
                                            </Link>
                                        </td>
                                        <td>{p.nom_categorie || '-'}</td>
                                        <td className="text-center fw-bold" style={{ color: '#21ACCD' }}>
                                            {p.quantite_reelle}
                                        </td>
                                        <td className="text-center">
                                            <Badge className="rounded-pill px-3" style={{ background: '#64748b' }}>
                                                {p.stock_maximum}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <span className="fw-bold text-warning">
                                                +{p.quantite_excedent}
                                            </span>
                                        </td>
                                        <td className="text-center" style={{ minWidth: '120px' }}>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="progress-bar-custom" style={{ width: '80px' }}>
                                                    <div className="progress-fill" style={{ width: `${Math.min(ratio, 100)}%`, background: '#21ACCD' }}></div>
                                                </div>
                                                <small className="text-muted">{ratio.toFixed(0)}%</small>
                                            </div>
                                        </td>
                                        <td className="fw-bold text-primary">
                                            {formatCurrency(p.valeur_stock)}
                                        </td>
                                        <td className="text-center pe-4">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                <Button 
                                                    variant="outline-info" 
                                                    size="sm"
                                                    className="action-btn"
                                                    onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                >
                                                    <i className="ri-eye-line"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center py-5">
                                    <div className="empty-state">
                                        <i className="ri-checkbox-circle-line empty-icon text-success" style={{ fontSize: '4rem' }}></i>
                                        <h5 className="mt-3">Aucun produit en surstock</h5>
                                        <p className="text-muted">Tous les stocks sont dans les limites recommandées</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
            `}</style>
        </>
    );
}